import {
  type Lead,
  LeadsApi,
  BusinessCardScanMimeType,
  type ScanBusinessCardResultDto,
} from '@up2stay/up2promo-api-client';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Loader2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  BusinessCardCameraModal,
  type BusinessCardCaptureResult,
} from '@/core/components/leads/business-card-camera-modal';
import { BusinessCardScanReviewDialog } from '@/core/components/leads/business-card-scan-review-dialog';
import { triggerErrorHaptic } from '@/core/lib/dnd-haptics';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

function toScanMimeType(
  mimeType: BusinessCardCaptureResult['mimeType'],
): BusinessCardScanMimeType {
  if (mimeType === 'image/png') return BusinessCardScanMimeType.IMAGE_PNG;
  if (mimeType === 'image/webp') return BusinessCardScanMimeType.IMAGE_WEBP;
  return BusinessCardScanMimeType.IMAGE_JPEG;
}

interface BusinessCardScanFlowProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingLeads?: Lead[];
  profileId?: string | null;
  workspaceId?: string | null;
  onLeadCreated: (leadId?: string) => void;
  eventIds?: string[];
}

export function BusinessCardScanFlow({
  open,
  onOpenChange,
  existingLeads = [],
  profileId,
  workspaceId,
  onLeadCreated,
  eventIds,
}: BusinessCardScanFlowProps) {
  const dict = useDictionary();
  const scanDict = dict.leads.businessCardScan as Record<string, unknown>;
  const fieldsDict = (scanDict.fields ?? {}) as Record<string, string>;

  const [step, setStep] = useState<'camera' | 'analyzing' | 'review'>('camera');
  const [scanResult, setScanResult] =
    useState<ScanBusinessCardResultDto | null>(null);
  const [capture, setCapture] = useState<BusinessCardCaptureResult | null>(
    null,
  );
  const [cameraSessionKey, setCameraSessionKey] = useState(0);

  const reset = useCallback(() => {
    setStep('camera');
    setScanResult(null);
    setCapture(null);
    setCameraSessionKey((key) => key + 1);
  }, []);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        reset();
      }
      onOpenChange(next);
    },
    [onOpenChange, reset],
  );

  const handleCapture = useCallback(
    async (nextCapture: BusinessCardCaptureResult) => {
      setCapture(nextCapture);
      setStep('analyzing');
      try {
        if (!workspaceId) {
          throw new Error('workspaceId required');
        }
        const response = await LeadsApi.scanBusinessCard({
          path: { workspaceId },
          body: {
            imageBase64: nextCapture.imageBase64,
            mimeType: toScanMimeType(nextCapture.mimeType),
          },
        });
        setScanResult(response.data ?? null);
        setStep('review');
      } catch (error) {
        console.error('Business card scan failed:', error);
        void triggerErrorHaptic();
        const apiError = (
          error as {
            response?: {
              status?: number;
              data?: { error?: string; message?: string };
            };
          }
        )?.response?.data;
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        const code = apiError?.error;
        if (status === 413 || code === 'imageTooLarge') {
          toast.error(String(scanDict.errorsImageTooLarge));
        } else if (code === 'openAiNotConfigured') {
          toast.error(String(scanDict.errorsOpenAi));
        } else {
          toast.error(apiError?.message ?? String(scanDict.errorsScanFailed));
        }
        setStep('camera');
        setCameraSessionKey((key) => key + 1);
      }
    },
    [
      scanDict.errorsImageTooLarge,
      scanDict.errorsOpenAi,
      scanDict.errorsScanFailed,
      workspaceId,
    ],
  );

  const reviewLabels = useMemo(
    () => ({
      title: String(scanDict.reviewTitle),
      description: String(scanDict.reviewDescription),
      found: String(scanDict.fieldFound),
      missing: String(scanDict.fieldMissing),
      requiredHint: String(scanDict.requiredHint),
      save: String(scanDict.saveLead),
      saving: String(scanDict.saving),
      retake: String(scanDict.retake),
      cancel: String(scanDict.cancel),
      firstNameRequired: String(scanDict.firstNameRequired),
      saveFailed: String(scanDict.saveFailed),
      uploadFailed: String(scanDict.errorsUploadFailed),
      saveWithoutAttachment: String(scanDict.saveWithoutAttachment),
    }),
    [scanDict],
  );

  const fieldLabels = useMemo(
    () => ({
      firstName: fieldsDict.firstName ?? 'First name',
      lastName: fieldsDict.lastName ?? 'Last name',
      email: fieldsDict.email ?? 'Email',
      phone: fieldsDict.phone ?? 'Phone',
      companyName: fieldsDict.companyName ?? 'Company',
      jobTitle: fieldsDict.jobTitle ?? 'Job title',
      website: fieldsDict.website ?? 'Website',
      address: fieldsDict.address ?? 'Address',
      notes: fieldsDict.notes ?? 'Notes',
    }),
    [
      fieldsDict.firstName,
      fieldsDict.lastName,
      fieldsDict.email,
      fieldsDict.phone,
      fieldsDict.companyName,
      fieldsDict.jobTitle,
      fieldsDict.website,
      fieldsDict.address,
      fieldsDict.notes,
    ],
  );

  return (
    <>
      <BusinessCardCameraModal
        key={cameraSessionKey}
        open={open && step === 'camera'}
        onOpenChange={(next) => {
          if (!next) handleOpenChange(false);
        }}
        onCapture={handleCapture}
        title={String(scanDict.title)}
        description={String(scanDict.description)}
        uploadDescription={String(scanDict.uploadDescription)}
        uploadLabel={String(
          isCapacitorNative()
            ? scanDict.uploadFromGallery
            : scanDict.uploadPhoto,
        )}
        captureLabel={String(scanDict.capture)}
        cancelLabel={String(scanDict.cancel)}
        cameraErrorLabel={String(scanDict.errorsCamera)}
        cameraErrorHint={String(scanDict.errorsCameraHint)}
        cameraErrorInsecureLabel={String(scanDict.errorsCameraInsecure)}
        cameraErrorPermissionLabel={String(
          isCapacitorNative()
            ? scanDict.errorsCameraPermission
            : scanDict.errorsCameraPermissionBrowser,
        )}
        cameraErrorNotFoundLabel={String(scanDict.errorsCameraNotFound)}
        statusSearching={String(scanDict.statusSearching)}
        statusCardDetected={String(scanDict.statusCardDetected)}
        statusHoldStill={String(scanDict.statusHoldStill)}
      />

      <ResponsiveDialog
        open={open && step === 'analyzing'}
        onOpenChange={() => {}}
      >
        <ResponsiveDialogContent>
          <div className="flex w-full flex-col items-center gap-4 py-8">
            <Loader2
              className="h-10 w-10 animate-spin text-primary"
              aria-hidden
            />
            <ResponsiveDialogHeader className="w-full text-center sm:text-center">
              <ResponsiveDialogTitle>
                {String(scanDict.analyzingTitle)}
              </ResponsiveDialogTitle>
              <ResponsiveDialogDescription>
                {String(scanDict.analyzingDescription)}
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <BusinessCardScanReviewDialog
        open={open && step === 'review'}
        onOpenChange={(next) => {
          if (!next) handleOpenChange(false);
        }}
        scanResult={scanResult}
        capture={capture}
        existingLeads={existingLeads}
        profileId={profileId}
        workspaceId={workspaceId}
        eventIds={eventIds}
        labels={reviewLabels}
        fieldLabels={fieldLabels}
        onRetake={() => {
          setScanResult(null);
          setCapture(null);
          setCameraSessionKey((key) => key + 1);
          setStep('camera');
        }}
        onLeadCreated={(leadId) => {
          reset();
          onOpenChange(false);
          onLeadCreated(leadId);
        }}
      />
    </>
  );
}
